<template>
  <div class="groups-container">
    <div class="content-header">
      <div class="header-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>
      <h1>Csoportok</h1>
      <p>{{ isTeacher && !isAdmin ? 'Hozzárendelt csoportok kezelése' : 'Csoportok kezelése és beállítása' }}</p>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Adatok betöltése...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <div class="error-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      </div>
      <h3>Hiba történt</h3>
      <p>{{ error }}</p>
    </div>

    <div v-else-if="data != null" class="groups-content">
      <div v-if="data.groups != null && data.groups.length > 0" class="groups-section">
        <div class="section-header">
          <h2>{{ isTeacher && !isAdmin ? 'Hozzárendelt csoportok' : 'Létrehozott csoportok' }}</h2>
          <p>{{ data.groups.length }} csoport található</p>
        </div>
        <GroupsTable :groups="data.groups" />
      </div>

      <div v-else class="empty-state">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <h3>{{ isTeacher && !isAdmin ? 'Nincs hozzárendelt csoport' : 'Nincs csoport létrehozva' }}</h3>
        <p>{{ isTeacher && !isAdmin ? 'Még nincs hozzárendelve csoport a tanárhoz' : 'Hozzon létre egy új csoportot a kezdéshez' }}</p>
      </div>

      <div v-if="isAdmin" class="actions-section">
        <router-link to="/settings/groups/new" class="btn btn-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Új csoport
        </router-link>
      </div>

      <!-- ✅ SCROLL TARGET -->
      <div class="router-content" ref="routerContent">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import GroupsTable from './GroupsTable.vue';
import { nextTick } from 'vue';

const day_map = {
  1: "Hétfő",
  2: "Kedd",
  3: "Szerda",
  4: "Csütörtök",
  5: "Péntek"
};

export default {
  components: { GroupsTable },

  data() {
    return {
      data: null,
      loading: true,
      error: null,
      isAdmin: false,
      isTeacher: false,
      userId: null
    };
  },

  mounted() {
    this.getIsAdmin();
    this.getIsTeacher();
    this.getUserId();
    this.fetchData();
  },

  watch: {
    // ✅ scroll when router-view changes to "new group"
    $route(to) {
      if (to.path === '/settings/groups/new') {
        nextTick(() => {
          this.$refs.routerContent?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        });
      }
    }
  },

  beforeRouteUpdate(to, from, next) {
    if (to.path === '/settings/groups' && from.path !== to.path) {
      this.fetchData();
    }
    next();
  },

  methods: {
    getIsAdmin() {
      this.isAdmin = this.$isAdmin();
    },
    getIsTeacher() {
      this.isTeacher = this.$isTeacher();
    },
    getUserId() {
      this.userId = this.$getUserId();
    },

    async fetchData() {
      try {
        let response;

        if (this.isTeacher && !this.isAdmin) {
          if (!this.userId) throw new Error('Teacher ID not found');
          response = await fetch(
            `${this.$BACKEND_HOST}/api/settings/teachers/groups?teacher_id=${this.userId}`
          );
        } else {
          response = await fetch(
            `${this.$BACKEND_HOST}/api/settings/groups/info`
          );
        }

        if (!response.ok) throw new Error('Failed to fetch data');

        const data = await response.json();

        if (data.groups) {
          data.groups.forEach(g => {
            if (g.schedule) {
              try {
                let sched = JSON.parse(g.schedule);
                if (!Array.isArray(sched)) sched = [sched];
                g.schedule = sched.map(s => ({ ...s, day: day_map[s.day] || s.day }));
              } catch (e) {
                // leave schedule as-is if parsing fails
              }
              g.isEditing = false;
            }
          });
        }

        this.data = data;
        this.loading = false;
      } catch (err) {
        this.error = err.message;
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.groups-container {
  padding: 0;
  max-width: 100%;
}

.content-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-border);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 12px;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.content-header h1 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.content-header p {
  color: var(--color-text-muted);
  font-size: 0.95rem;
  margin: 0;
  margin-left: auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: var(--color-text-muted);
  font-size: 1rem;
  margin: 0;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  text-align: center;
}

.error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: var(--error);
  border-radius: 50%;
  color: white;
  box-shadow: var(--shadow-lg);
}

.error-container h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.error-container p {
  color: var(--color-text-muted);
  font-size: 1rem;
  margin: 0;
}

.groups-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.groups-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.section-header h2 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.section-header p {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background: var(--color-background-mute);
  border-radius: 50%;
  color: var(--color-text-muted);
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm);
}

.empty-state h3 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: var(--color-text-muted);
  font-size: 1rem;
  margin: 0;
}

.actions-section {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
}

/* Button styles moved to components.css */

.router-content {
  margin-top: 1rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .content-header p {
    margin-left: 0;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
  }
  
  .empty-state {
    padding: 3rem 1.5rem;
  }
  
  .empty-icon {
    width: 80px;
    height: 80px;
  }
  
  .empty-state h3 {
    font-size: 1.25rem;
  }
  
  .empty-state p {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .content-header h1 {
    font-size: 1.25rem;
  }
  
  .content-header p {
    font-size: 0.875rem;
  }
  
  .section-header {
    padding: 0.75rem 1rem;
  }
  
  .section-header h2 {
    font-size: 1.125rem;
  }
  
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .empty-icon {
    width: 70px;
    height: 70px;
  }
  
  .empty-state h3 {
    font-size: 1.125rem;
  }
  
  .empty-state p {
    font-size: 0.875rem;
  }
  
  .btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.8rem;
  }
}
</style>
  